import { unformat, toFixed } from 'accounting';
import {
  get,
  isFinite,
  isNil,
  isEqual,
  sumBy,
  first,
  last,
  cloneDeep,
} from 'lodash/fp';
import { DealerCashRuleType } from './legos/constants';
import { CreditRange, Deal } from './legos/dealTypes';
import { FinalFee, FinalFeeType } from './legos/financingTypes';
import {
  FinanceTypeToFullOfferType,
  FullLeaseOffer,
  FullOffer,
  FullPurchaseOffer,
  Payments,
} from './legos/paymentTypes';
import { isPurchase, isLease, isCash } from './validation/financeType';
import { fmtMoney } from './formatter';
import { FinanceType, DEFAULT_DOWN_PAYMENT } from './constants';
import { isFinanceTypeAllowedForCarWithDeal } from './deal-utils';
import { GetAllFinanceTypesForPaymentOffers } from './types/paymentTypes';
import { OfferData } from './legos/deskingTypes';

// @TODO add proper types
type Car = any;
type Offer = any;
type Dealer = any;
type OfferValueProp = string | null;

export const getDefaultDownPayment = (carPrice: number, dealer: Dealer) => {
  if (!isNil(carPrice)) {
    if (dealer?.featureConfig?.downPayment?.downPaymentOptions) {
      if (
        dealer?.featureConfig?.downPayment?.downPaymentOptions?.isPercentage
      ) {
        return Math.round(
          carPrice *
            dealer?.featureConfig?.downPayment?.downPaymentOptions
              ?.percentageDownPaymentA,
        );
      }

      return dealer?.featureConfig?.downPayment?.downPaymentOptions
        ?.fixedDownPaymentA;
    }

    return Math.round(
      carPrice *
        (dealer?.featureConfig?.downPayment?.default ?? DEFAULT_DOWN_PAYMENT),
    );
  }

  return 0;
};

export const getDealerCashRuleAppliedValue = (offer: any) =>
  offer?.dealerCashRule?.type === DealerCashRuleType.PRICE_OFFSET_PERCENT
    ? ((offer?.dealerCashRule?.value || 0) / 100) * (offer?.dealerCash || 0)
    : Math.min(offer?.dealerCashRule?.value || 0, offer?.dealerCash || 0);

export const getPaymentOffers = <T extends FinanceType>(
  payments: Payments,
  financeType: T,
): FinanceTypeToFullOfferType<T>[] => {
  const offers = payments.allOffers.find(o => o.financeType === financeType)
    ?.offers;
  return (offers || []) as FinanceTypeToFullOfferType<T>[];
};

/**
 * Given a payments object and offer from a pencil, get the associated payments offer
 * @param payments
 * @param financeType
 * @param selectedOffer
 * @returns
 */
export const getPaymentOffer = <T extends FinanceType>(
  payments: Payments | null | undefined,
  financeType: T,
  selectedOffer?: OfferData | FullOffer | Offer,
): FinanceTypeToFullOfferType<T> | undefined => {
  if (!payments) {
    return undefined;
  }
  const { months, mileage, apr, rate, category } = selectedOffer || {};
  const offers = getPaymentOffers(payments, financeType);
  const deskingOfferIds = offers.map(offer => offer.deskingOfferId);

  if (
    selectedOffer?.deskingOfferId &&
    deskingOfferIds.includes(selectedOffer.deskingOfferId)
  ) {
    // If offer has a desking offer ID and its included in the offers desking IDs, then use that exclusively
    // This will only happen for a desking V2 pencil
    return offers.find(
      (offer: any) => offer.deskingOfferId === selectedOffer.deskingOfferId,
    );
  } else if (isPurchase(financeType)) {
    // find purchase offers by month, apr and category
    return offers.find(
      (offer: Offer) =>
        offer.months === months &&
        offer.apr === apr &&
        isEqual(offer.category)(category),
    );
  } else if (isLease(financeType)) {
    // find lease offers by month, mileage, money factor and category
    return offers.find(
      (offer: Offer) =>
        offer.months === months &&
        offer.mileage === mileage &&
        offer.rate === rate &&
        isEqual(offer.category)(category),
    );
  }

  // cash should only have one offer
  return get('0', offers);
};

export const getPeriodicPaymentFromOffer = (
  offer: any,
  useBiweekly: boolean,
) => {
  if (useBiweekly) {
    return isFinite(offer.estimatedMonthlyPayment)
      ? offer.estimatedMonthlyPayment / 2
      : offer.estimatedMonthlyPayment;
  }
  return offer.estimatedMonthlyPayment;
};

// Return the Estimated Periodic Payment for a Deal
// Considers offer and dealer configuration
export const getPeriodicPayment = ({
  dealer,
  offer,
  offerValueProp,
}: {
  dealer?: Dealer;
  offer?: Offer;
  offerValueProp?: OfferValueProp;
}) => {
  if (!offerValueProp) {
    // eslint-disable-next-line no-param-reassign
    offerValueProp = 'estimatedMonthlyPayment';
  }

  const useBiweekly = get('desking.showBiweeklyPayments')(dealer);
  let value = (offer || {})[offerValueProp];

  if (!isFinite(value)) {
    return { payment: 0 };
  }

  if (useBiweekly) {
    if (isFinite(value)) {
      value /= 2;
    }
  }

  return { payment: fmtMoney(value || 0) };
};

/**
 * Converts a residual percent to a residual value
 *
 * Typical formula for residual value:
 *   Residual Value = (Residual Percentage) * MSRP
 * If MRM exists and MRM < MSRP:
 *   Residual Value = (Residual Percentage) * MRM
 */
export const getResidualValueFromPercent = (
  dealer: Dealer,
  car: Car,
  mrm: number | null | undefined,
  percentage: number,
  msrpOverride?: number,
) => {
  // Some dealers (Ford's, Jordan Toyota) have MRM disabled
  const ignoreMRM = dealer.desking?.ignoreMRM ?? false;
  const msrp = msrpOverride || car.msrp;
  const { price } = car;
  if (msrp) {
    if (ignoreMRM) {
      return unformat(toFixed((msrp * percentage) / 100, 2));
    } else if (mrm && mrm < msrp) {
      return unformat(toFixed((mrm * percentage) / 100, 2));
    }
    return unformat(toFixed((msrp * percentage) / 100, 2));
  } else if (price) {
    return unformat(toFixed((price * percentage) / 100, 2));
  }
  return 0;
};

export const isOnePayLease = ({
  deal,
  dealer,
  financeType,
  onePayLeaseSelected,
}: {
  deal: Deal;
  dealer?: Dealer;
  financeType?: FinanceType;
  onePayLeaseSelected?: boolean;
}) => {
  // If dealer is provided and dealer has not enabled One Pay Lease, return false
  if (dealer && !dealer?.desking?.enableOnePayLease) {
    return false;
  }
  // Only takes into account desking flag and received one pay lease.
  if (!deal && !financeType) {
    return onePayLeaseSelected;
  }
  const financeCondition = financeType || deal?.financeType;
  const onePayLeaseSelectedStatus = onePayLeaseSelected || deal?.onePayLease;
  return isLease(financeCondition) && onePayLeaseSelectedStatus;
};

export const getFinanceResponseTotalAPR = (financeResponse: any) => {
  const apr = financeResponse?.data?.apr ?? 0;
  const markup = financeResponse?.data?.markup ?? 0;
  return apr + markup;
};

export const calculateFinalFeesTotal = (finalizedFeeList?: FinalFee[]) => {
  return (
    sumBy('value')(
      finalizedFeeList?.filter(
        f => f.type !== FinalFeeType.FINANCED_LIEN_NOTATION,
      ),
    ) || 0
  );
};

/**
 * Return all allowed financeTypes available from on the payments object
 */
export const getAllFinanceTypesForPaymentOffers = ({
  payments,
  car,
  deal,
  dealer,
  clientType,
}: GetAllFinanceTypesForPaymentOffers) =>
  Object.values(FinanceType).filter(financeType => {
    const isAllowed = isFinanceTypeAllowedForCarWithDeal({
      car,
      deal,
      dealer,
      clientType,
      financeType,
    });

    if (isAllowed) {
      const offersForFinanceType = payments?.allOffers.find(
        offer => offer.financeType === financeType,
      );
      return !!offersForFinanceType && offersForFinanceType.offers.length > 0;
    }

    return false;
  });

/**
 * Return all allowed financeTypes available from on the payments object
 */
export const getAllFinanceTypesForPaymentOffersForWebClient = (
  args: GetAllFinanceTypesForPaymentOffers,
) => {
  const financeTypes = getAllFinanceTypesForPaymentOffers(args);
  /**
   * if purchase is not available, add it to the list of finance types.
   * So that downpayment field will be always available in the UI.
   * This change is made as part of https://upstartnetwork.atlassian.net/browse/REL-17721
   */
  if (!financeTypes.includes(FinanceType.PURCHASE)) {
    financeTypes.unshift(FinanceType.PURCHASE);
  }

  return financeTypes;
};

export const modifyDealWithDefaultOfferAndOtherDetails = ({
  payments,
  deal,
  creditRange,
  financeType,
  dealer,
}: {
  payments: Payments;
  deal: Deal;
  creditRange?: CreditRange;
  financeType: FinanceType;
  dealer: Dealer;
}): Deal => {
  let defaultSelectedOffer: Offer | FullOffer | OfferData | undefined;
  if (financeType === FinanceType.LEASE) {
    const offers = getPaymentOffers(payments, financeType);
    defaultSelectedOffer = last(offers);
    const initialValue = first(offers);
    if (initialValue) {
      defaultSelectedOffer = offers.reduce(
        (acc: FullLeaseOffer, o: FullLeaseOffer) => {
          if (o.estimatedMonthlyPayment < acc.estimatedMonthlyPayment) {
            return o;
          }
          return acc;
        },
        initialValue,
      );
    }
  } else if (
    financeType === FinanceType.PURCHASE &&
    dealer?.desking?.showSeparateLowInterestHighRebateOffers?.enabled
  ) {
    const offers = getPaymentOffers(payments, financeType);
    const defaultLowestInterestHighestRebateOffer = offers.find(
      (o: FullPurchaseOffer) =>
        o?.category?.type ===
        dealer?.desking?.showSeparateLowInterestHighRebateOffers?.defaultOffer,
    );

    if (defaultLowestInterestHighestRebateOffer) {
      defaultSelectedOffer = defaultLowestInterestHighestRebateOffer;
    }
  }

  const selectedMileage =
    isLease(financeType) && defaultSelectedOffer?.mileage
      ? defaultSelectedOffer?.mileage
      : null;

  const term =
    isCash(financeType) && !defaultSelectedOffer?.months
      ? null
      : defaultSelectedOffer?.months;

  const newDeal: Deal = {
    ...deal,
    selectedMileage,
    financeType,
    creditRange: creditRange || deal.creditRange,
    selectedOffer: defaultSelectedOffer,
    selectedProgramOffers: [],
    term,
  };

  return newDeal;
};

const removeEmptyMSOffersFromPayments = (
  payments: Payments,
  financeType: FinanceType,
) => {
  const paymentsCloned = cloneDeep(payments);

  const paymentObject = paymentsCloned.allOffers.find(
    payment => payment.financeType === financeType,
  );

  if (paymentObject && paymentObject.offers) {
    paymentObject.offers = (paymentObject.offers as any).filter(
      (payment: FullPurchaseOffer | FullLeaseOffer) => !payment.emptyPayment,
    );
  }

  return paymentsCloned;
};

export const removeEmptyMSLeaseOffersFromPayments = (payments: Payments) => {
  return removeEmptyMSOffersFromPayments(payments, FinanceType.LEASE);
};

export const removeEmptyMSPurchaseAndLeaseOffersFromPayments = (
  payments: Payments,
) => {
  const sanitizedPayments = removeEmptyMSOffersFromPayments(
    payments,
    FinanceType.PURCHASE,
  );
  return removeEmptyMSLeaseOffersFromPayments(sanitizedPayments);
};
